import { deleteDoc, doc } from 'firebase/firestore'
import { deleteObject, ref } from 'firebase/storage'
import { isEmpty } from 'lodash'
import React, { useEffect } from 'react'

import { Card } from '@components/card.component'
import { FileUpload } from '@components/file-upload.component'
import { Icon } from '@components/icon.component'
import { Button } from '@components/mantine/button.component'
import { useUpload } from '@hooks/use-upload.hook'
import { useAppState } from '@src/app.state'
import { db, storage } from '@src/firebase-app'
import { FieldHeaderContent } from '@src/pages/workflow/client/fields/field-header-content.component'
import { dbNames } from '@utils/constants'
import { toastService } from '@utils/toast.service'
import { Control, useController } from 'react-hook-form'
import { fileIsImage } from '../fields.utils'

type WorkflowFileUploadProps = {
  field: Field
  readOnly?: boolean
  workflow?: any
  section: ActivitySection
  isRequired?: boolean
  suspendUpload?: boolean
  iterationId?: string
  control: Control
  uploadRef?: React.MutableRefObject<any>
}

export function WorkflowFileUpload({
  field,
  readOnly,
  workflow = {},
  section,
  isRequired,
  suspendUpload,
  iterationId,
  control,
  uploadRef,
}: WorkflowFileUploadProps) {
  const user = useAppState(state => state.user)

  const {
    field: { onChange, ...fieldProps },
    fieldState: { error },
  } = useController({
    name: `${field.version}_${field.id}`,
    control,
    rules: {
      validate: (v: any) => {
        if (isEmpty(v)) {
          return 'This field is required'
        }
      },
    },
  })

  const {
    doUpload,
    uploading,
    registeredFiles,
    registerFiles,
    deregisterFiles,
    deregisterAllFiles,
  } = useUpload({
    user,
    uploadToType: 'workflow',
    uploadToPath: `${dbNames.activities}/${workflow.id}/${dbNames.activitySections}/${section.id}/${dbNames.sectionIterations}/${iterationId}/fields/${field.id}`,
  })

  useEffect(() => {
    if (!uploadRef) return
    uploadRef.current = {
      doUpload,
    }
  }, [registeredFiles, uploadRef])

  function handleFileSelection({ files }: { files: any[] }) {
    if (readOnly) return
    if (suspendUpload) {
      registerFiles(files)
      onChange(files)
    } else {
      registerFiles(files)
    }
  }

  async function handleFileUpload() {
    const result = await doUpload()
    deregisterAllFiles()
    const newUploaded = [...fieldProps.value, ...result].filter(
      (f: any) => !(f instanceof File),
    )
    onChange(newUploaded)
  }

  async function handleDeleteFile(path: string, fileId: string) {
    const fileRef = ref(storage, path)
    const updatedFileList = fieldProps.value.filter(
      (uploadedFile: any) => uploadedFile.path !== path,
    )

    try {
      await deleteObject(fileRef)
      await deleteDoc(doc(db, dbNames.files, fileId))
    } catch (err) {
      await deleteDoc(doc(db, dbNames.files, fileId))
    }

    onChange(updatedFileList)
  }

  function handleRemoveFromUpload(fileUIDs: string[]) {
    const newFiles = deregisterFiles(fileUIDs)
    onChange(newFiles)
  }

  function handleCancelUpload() {
    deregisterAllFiles()
  }

  function displayUpload() {
    const filesRegistered = !isEmpty(registeredFiles)
    if (suspendUpload) return false
    return filesRegistered && !uploading
  }

  return (
    <div>
      <FieldHeaderContent
        field={field}
        isRequired={isRequired}
        section={section}
      />
      <div className="flex flex-col">
        {!isEmpty(registeredFiles) && (
          <div className="grid grid-cols-[repeat(auto-fill,10rem)] auto-rows-[10rem] gap-3 mb-5">
            {registeredFiles.map(({ preview, file, uid }: any, index: number) => {
              if (!file) return null
              if (file.type.includes('image')) {
                return (
                  <div
                    key={index}
                    className="relative rounded"
                  >
                    <img
                      src={preview}
                      alt="image"
                      className="object-cover h-full w-full rounded shadow"
                    />
                    {!uploading && (
                      <button
                        className="absolute top-1 right-1 flex items-center justify-center rounded-full h-8 w-8 bg-transparent text-white cursor-pointer border-0 p-0"
                        onClick={e => {
                          e.preventDefault()
                          handleRemoveFromUpload([uid])
                        }}
                      >
                        <Icon
                          name="circle-xmark"
                          size={18}
                        />
                      </button>
                    )}
                  </div>
                )
              } else {
                return (
                  <div
                    key={index}
                    className="relative rounded"
                  >
                    <div className="flex items-center text-center h-full w-full rounded shadow">{file.name}</div>
                    {!uploading && (
                      <button
                        className="absolute top-1 right-1 flex items-center justify-center rounded-full h-8 w-8 bg-transparent text-gray-700 cursor-pointer border-0 p-0"
                        onClick={e => {
                          e.preventDefault()
                          handleRemoveFromUpload([uid])
                        }}
                      >
                        <Icon
                          name="circle-xmark"
                          size={18}
                        />
                      </button>
                    )}
                  </div>
                )
              }
            })}
          </div>
        )}
        <div className="flex justify-between">
          <FileUpload
            {...({
              btnVariant: 'secondary',
              readOnly: readOnly || uploading,
              onChange: handleFileSelection,
              multiple: true,
              error: error?.message,
              ...fieldProps,
            } as any)}
          />
          {displayUpload() && (
            <div className="mt-5">
              <Button
                className="mr-2"
                onClick={handleCancelUpload}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleFileUpload}
              >
                Upload
              </Button>
            </div>
          )}
        </div>
        {!isEmpty(fieldProps.value) && !suspendUpload && (
          <div className="grid grid-cols-[repeat(auto-fill,10rem)] auto-rows-[10rem] gap-3 mt-5">
            {fieldProps.value.map(({ path, name, url, type, fileId }: any) => {
              if (!type) return null
              if (fileIsImage({ type } as any)) {
                return (
                  <a
                    key={`${path}_${fileId}`}
                    className="w-full h-full relative rounded hover:no-underline"
                    href={url}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    <img
                      className="h-full w-full object-cover rounded transition-colors hover:bg-gray-50"
                      src={url}
                      alt={name}
                    />
                    {!readOnly && (
                      <button
                        className="absolute top-1 right-1 flex items-center justify-center rounded-full h-8 w-8 bg-transparent text-white cursor-pointer border-0 p-0"
                        onClick={e => {
                          e.preventDefault()
                          handleDeleteFile(path, fileId).then(() => {
                            toastService.info('Successfully deleted file.')
                          })
                        }}
                      >
                        <Icon
                          name="circle-xmark"
                          size={18}
                        />
                      </button>
                    )}
                  </a>
                )
              }
              return (
                <a
                  key={path}
                  className="w-full h-full relative rounded hover:no-underline"
                  href={url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <Card className="h-full w-full rounded flex items-center text-center transition-colors hover:bg-gray-50">
                    {name}
                    {!readOnly && (
                      <button
                        className="absolute top-1 right-1 flex items-center justify-center rounded-full h-8 w-8 bg-transparent text-gray-700 cursor-pointer border-0 p-0"
                        onClick={e => {
                          e.preventDefault()
                          handleDeleteFile(path, fileId).then(() => {
                            toastService.info('Successfully deleted file.')
                          })
                        }}
                      >
                        <Icon
                          name="circle-xmark"
                          size={18}
                        />
                      </button>
                    )}
                  </Card>
                </a>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
